import React, {useState} from 'react';

import {graphql, useStaticQuery} from 'gatsby';
import {Events} from 'src/analytics';
import {EmailBannerSideBar} from 'src/components/editorial';
import {SEOWithQuery} from 'src/components/shared';
import {StickySocialLinks} from 'src/components/shared/StickySocialLinks';
import {StickySocialLinksMobile} from 'src/components/shared/StickySocialLinksMobile';
import {CATEGORY, PODCAST} from 'src/routes/paths';
import {editorial as copy, seoTitlePrefix} from 'src/utils/copy';
import styled from 'styled-components';

import {
  Maybe,
  SanityBlock,
  SanityEditorial,
  SanityImageWithAlt,
  SanityGlobalPodcastList,
  SanityPodcastLink,
} from 'types/generated';

import {InsightsNavigation} from '../insightsNavigation';
import {MoreFromPodcasts} from './MoreFromPodcasts';
import {PodcastRichContent} from './PodcastContent';
import {PodcastPlayer} from './PodcastPlayer';
import {SupportingContent} from './SupportingContent';

interface PodcastArticleProps {
  podcastEditorial: SanityEditorial;
  megaphoneLink: string;
  podcastDescription?: Maybe<Array<Maybe<SanityBlock>>>;
  podcastTranscript?: Maybe<Array<Maybe<SanityBlock>>>;
  episodeImage?: Maybe<SanityImageWithAlt>;
  relatedArticles?: [Maybe<SanityEditorial>];
  next?: {
    slug: string;
    title: Maybe<string>;
    episodeImage: Maybe<SanityImageWithAlt>;
    content: JSON;
    editorialCategory: JSON;
    category: Maybe<string>;
  };
  href: string;
  isDraft?: boolean;
}

const Content = styled(PodcastRichContent)``;

const trackPodcastSubscribe = (medium: string, form: string, section: string) => {
  Events.didSubscribePodcast({medium, form, section});
};

const renderPodcastFollow = (globalPodcastContent: SanityGlobalPodcastList) => {
  return (
    <div className="w-full mt-8 lg:mt-5 flex items-center flex-wrap space-y-2">
      <span className="type-lota md:mr-4 block w-full lg:w-auto">Follow:</span>
      {globalPodcastContent?.podcastLinks?.map((item: Maybe<SanityPodcastLink>) => {
        return (
          <a
            href={item?.podcastUrl || ''}
            target="_blank"
            rel="noreferrer"
            key={item?.podcastName}
            onClick={() => trackPodcastSubscribe(item?.podcastName || 'Podcast', 'Follow Podcast', 'Podcast Subscribe')}
          >
            <div className="button-white mr-2">
              {item?.podcastIcon?.asset?.url && (
                <object type="image/svg+xml" data={item.podcastIcon.asset.url}></object>
              )}{' '}
              <span className="ml-2 block">{item?.podcastName}</span>
            </div>
          </a>
        );
      })}
    </div>
  );
};
export const PodcastArticleTemplate: React.FC<PodcastArticleProps> = props => {
  const data = useStaticQuery(graphql`
    query PodcastArticleQuery {
      sanityGlobalPodcastList {
        subCopy
        title
        _rawListFooterComponents(resolveReferences: {maxDepth: 8})
        heroImage: _rawHeroImage(resolveReferences: {maxDepth: 8})
        seo: _rawSeo
        podcastLinks: _rawPodcastLinks(resolveReferences: {maxDepth: 8})
        listFooterComponents: _rawListFooterComponents(resolveReferences: {maxDepth: 8})
      }
    }
  `);

  const globalPodcastContent = data.sanityGlobalPodcastList;
  const [isDescription, setIsDescription] = useState(true);
  const date = copy.mostRecentPublishDate(props.podcastEditorial.publishDate);

  const hasCategory = props.podcastEditorial.editorialCategory?.slug;
  return (
    <>
      <SEOWithQuery
        title={props.podcastEditorial.title || 'Podcasts'}
        description={props.podcastEditorial.podcastDescription?.toString() || undefined}
        image={props.podcastEditorial?.episodeImage?.asset?.url || undefined}
      />
      <InsightsNavigation bgColor={'bg-ivory'} />
      <section className="relative px-gutter bg-ivory py-12" data-module="podcast-hero">
        <div className="wrapper-default grid grid-cols-4 md:grid-cols-12 lg:space-x-8">
          <div className="col-span-1 sticky top-36 h-[230px] hidden lg:block">
            <StickySocialLinks
              url={props.href}
              title={props.podcastEditorial.title || globalPodcastContent?.title || 'New Rules of Business Podcast'}
              description={props.podcastEditorial.description ?? globalPodcastContent?.subCopy}
              color={'white'}
              classes={'flex-col md:mt-7 lg:mt-10 hidden space-y-3 md:flex'}
            />
          </div>

          <div className="col-span-full lg:col-span-11 lg:col-start-2 flex flex-col">
            <div className="flex mb-2 lg:mb-4">
              <a href={`${PODCAST}`} className="type-eyebrow text-green-dark link-eyebrow">
                {globalPodcastContent?.title || ''}
              </a>

              {hasCategory && (
                <span
                  className={`type-eyebrow block text-green-dark ${hasCategory && 'border-l border-black ml-4 pl-4'} `}
                >
                  <a
                    href={`${CATEGORY}/${props?.podcastEditorial?.editorialCategory?.slug?.current}`}
                    className="type-eyebrow block text-green-dark link-eyebrow"
                  >
                    {props.podcastEditorial.editorialCategory?.title}
                  </a>
                </span>
              )}
            </div>
            <h1 className="type-alpha">{props.podcastEditorial.title}</h1>
            {date && <span className="type-sm-p mt-4">{date}</span>}

            <div className="w-full mt-8 flex flex-wrap">
              <div className="flex w-full space-x-4 lg:space-x-8 items-center lg:items-start">
                <div className="w-3/12 max-w-[190px] drop-shadow-2xl">
                  <div className="relative pb-full">
                    {props.episodeImage && (
                      <img
                        className="absolute object-cover w-full h-full"
                        src={props.episodeImage.asset?.url || ''}
                        alt={props.episodeImage.asset?.altText || ''}
                      />
                    )}
                  </div>
                </div>

                <div className="w-full flex" data-podcast-player>
                  <div className="flex w-full">
                    <div className="w-full">
                      <PodcastPlayer megaphoneLink={props.megaphoneLink} />
                      <div className="w-full hidden lg:block">{renderPodcastFollow(globalPodcastContent)}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile Follow CTA's */}
              <div className="w-full lg:hidden">{renderPodcastFollow(globalPodcastContent)}</div>
            </div>

            <div className="w-full lg:hidden flex space-x-3 mt-6">
              <StickySocialLinksMobile url={''} title={''} description={''} />
            </div>

            {/* Tab Section */}
            <div className="w-full mt-10 lg:mt-20 grid lg:grid-cols-12 lg:gap-x-8">
              <div className="col-span-full lg:col-span-8">
                <ul className="type-lota flex ml-4">
                  <button
                    onClick={() => setIsDescription(true)}
                    className={
                      'px-4 py-2 cursor-pointer hover:underline ' +
                      (isDescription ? ' active-tab text-black ' : ' !text-green-dark  ')
                    }
                  >
                    Episode Description
                  </button>
                  <button
                    onClick={() => setIsDescription(false)}
                    className={
                      'px-4 py-2 cursor-pointer hover:underline ' +
                      (isDescription ? ' !text-green-dark ' : ' active-tab text-black')
                    }
                  >
                    View Transcript
                  </button>
                </ul>
                <div className="text-gray prose border-t border-beige pt-6">
                  {isDescription && props.podcastDescription && (
                    <div data-tab-content="1" data-tab-active>
                      {!!props.podcastDescription && <Content blocks={props.podcastDescription} />}
                    </div>
                  )}
                  {!isDescription && (
                    <div data-tab-content="2">
                      {!!props.podcastTranscript && <Content blocks={props.podcastTranscript} />}
                    </div>
                  )}
                </div>
              </div>
              <EmailBannerSideBar
                class={'col-span-full py-5 lg:py-0 lg:col-span-4 lg:col-start-9'}
                id="newsletter-form"
              />
            </div>
          </div>
        </div>
      </section>
      <SEOWithQuery {...getSEOMeta(props.podcastEditorial, props.episodeImage)} url={props.href} />
      {props.relatedArticles && props.relatedArticles?.length > 0 ? (
        <MoreFromPodcasts relatedArticles={props.relatedArticles} heading={undefined} subcopy={undefined} />
      ) : null}
      {!!props.podcastEditorial.supportingContent && !props.isDraft && (
        <SupportingContent
          supportingContent={props.podcastEditorial.supportingContent}
          nextUpFeaturedDefault={props.next}
        />
      )}
    </>
  );
};

function getSEOMeta(podcastEditorial: SanityEditorial, episodeImage: Maybe<SanityImageWithAlt>) {
  const episodeImageUrl = episodeImage ? episodeImage?.asset?.url : null;
  const seoProps = {
    title: `${seoTitlePrefix} ${podcastEditorial.title}`,
    ...(typeof podcastEditorial.description === 'string' && {description: podcastEditorial.description}),
    ...(typeof episodeImageUrl === 'string' && {image: episodeImageUrl}),
    ...(typeof podcastEditorial.publishDate === 'string' && {publishDate: podcastEditorial.publishDate}),
    ...(Array.isArray(podcastEditorial.tags) && {tags: podcastEditorial.tags as string[]}),
  };
  return seoProps;
}
